import React, { useMemo, useState } from "react";

const MOCK_CUSTOMERS = [
  { id: 1, name: "Delta Aviation Services" },
  { id: 2, name: "JetStream Holdings" },
  { id: 3, name: "SkyFleet Partners" },
  { id: 4, name: "Atlantic Flight Ops" },
];

const MOCK_VENDORS = [
  { id: 101, name: "Sunshine Detailing Group" },
  { id: 102, name: "AeroClean Solutions" },
  { id: 103, name: "Runway Support Services" },
  { id: 104, name: "BlueWing Contractors" },
];

const USER_TYPE_OPTIONS = [
  {
    id: "internal",
    label: "Internal",
    description: "Operations, account managers, admins, and internal staff.",
  },
  {
    id: "customer",
    label: "Customer",
    description:
      "Users who belong to a customer account and manage visibility for their jobs.",
  },
  {
    id: "vendor",
    label: "Vendor",
    description:
      "Users who belong to a vendor organization and work assigned jobs.",
  },
];

const ROLE_OPTIONS = {
  internal: [
    {
      id: "admin",
      label: "Admin",
      description:
        "Full administrative access to manage users, operations, and account configuration.",
      tone: "red",
    },
    {
      id: "super_user",
      label: "Super User",
      description:
        "Broad operational visibility and elevated permissions across the platform.",
      tone: "amber",
    },
    {
      id: "account_manager",
      label: "Account Manager",
      description:
        "Manages customer relationships, accounts, and assigned workflows.",
      tone: "blue",
    },
    {
      id: "internal_coordinator",
      label: "Internal Coordinator",
      description:
        "Coordinates scheduling, assignments, and day-to-day operational work.",
      tone: "emerald",
    },
    {
      id: "master_pm",
      label: "Master PM",
      description:
        "Oversees project management and delivery responsibilities across teams.",
      tone: "violet",
    },
  ],
  customer: [
    {
      id: "customer_admin",
      label: "Customer Admin",
      description:
        "Primary admin for the customer account with access to manage users and visibility.",
      tone: "blue",
    },
    {
      id: "project_manager",
      label: "Project Manager",
      description:
        "Manages jobs, execution details, and communication for assigned work.",
      tone: "emerald",
    },
    {
      id: "customer_viewer",
      label: "Viewer",
      description:
        "Read-only visibility into jobs, statuses, and related activity.",
      tone: "slate",
    },
  ],
  vendor: [
    {
      id: "vendor_admin",
      label: "Vendor Admin",
      description:
        "Manages vendor users, assignments, and vendor-side operational access.",
      tone: "amber",
    },
    {
      id: "vendor_dispatcher",
      label: "Dispatcher",
      description:
        "Coordinates vendor staffing, assignments, and execution readiness.",
      tone: "blue",
    },
    {
      id: "vendor_user",
      label: "Vendor User",
      description: "Standard vendor access to view and work assigned jobs.",
      tone: "emerald",
    },
  ],
};

const STEP_TITLES = [
  "User Type",
  "Association",
  "Role & Access",
  "User Details",
  "Review",
];

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function getToneClasses(tone) {
  const map = {
    red: {
      soft: "bg-red-50 text-red-700 ring-red-200",
      solid: "bg-red-600 text-white",
      border: "border-red-200",
      dot: "bg-red-500",
    },
    amber: {
      soft: "bg-amber-50 text-amber-700 ring-amber-200",
      solid: "bg-amber-600 text-white",
      border: "border-amber-200",
      dot: "bg-amber-500",
    },
    blue: {
      soft: "bg-blue-50 text-blue-700 ring-blue-200",
      solid: "bg-blue-600 text-white",
      border: "border-blue-200",
      dot: "bg-blue-500",
    },
    emerald: {
      soft: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      solid: "bg-emerald-600 text-white",
      border: "border-emerald-200",
      dot: "bg-emerald-500",
    },
    violet: {
      soft: "bg-violet-50 text-violet-700 ring-violet-200",
      solid: "bg-violet-600 text-white",
      border: "border-violet-200",
      dot: "bg-violet-500",
    },
    slate: {
      soft: "bg-slate-50 text-slate-700 ring-slate-200",
      solid: "bg-slate-700 text-white",
      border: "border-slate-200",
      dot: "bg-slate-500",
    },
  };

  return map[tone] || map.slate;
}

function Input({ label, error, required, ...props }) {
  return (
    <div>
      <label className="mb-2 block text-sm font-semibold text-slate-700">
        {label}
        {required ? <span className="ml-1 text-red-500">*</span> : null}
      </label>
      <input
        {...props}
        className={cx(
          "w-full rounded-xl border bg-white px-4 py-3 text-sm text-slate-900 shadow-sm outline-none transition",
          "placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-100",
          error ? "border-red-300" : "border-slate-200",
        )}
      />
      {error ? (
        <p className="mt-2 text-xs font-medium text-red-600">{error}</p>
      ) : null}
    </div>
  );
}

function Toggle({ checked, onChange, label, description }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex w-full items-start justify-between rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:border-slate-300 hover:shadow-md"
    >
      <div className="pr-4">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        {description ? (
          <div className="mt-1 text-sm text-slate-500">{description}</div>
        ) : null}
      </div>

      <div
        className={cx(
          "relative mt-0.5 h-6 w-11 rounded-full transition",
          checked ? "bg-blue-600" : "bg-slate-300",
        )}
      >
        <div
          className={cx(
            "absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition",
            checked ? "left-5" : "left-0.5",
          )}
        />
      </div>
    </button>
  );
}

function StepBadge({ number, title, active, complete }) {
  return (
    <div className="flex items-center gap-3">
      <div
        className={cx(
          "flex h-9 w-9 items-center justify-center rounded-full text-sm font-bold transition",
          complete
            ? "bg-emerald-600 text-white"
            : active
              ? "bg-blue-600 text-white"
              : "bg-slate-100 text-slate-500",
        )}
      >
        {complete ? "✓" : number}
      </div>
      <div className="min-w-0">
        <div
          className={cx(
            "truncate text-sm font-semibold",
            active || complete ? "text-slate-900" : "text-slate-500",
          )}
        >
          {title}
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-slate-100 py-3 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-4">
      <div className="text-sm font-semibold text-slate-500">{label}</div>
      <div className="text-sm text-slate-900">{value || "—"}</div>
    </div>
  );
}

export default function UserCreationWizardMockup() {
  const [step, setStep] = useState(1);
  const [associationSearch, setAssociationSearch] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [form, setForm] = useState({
    userType: "",
    associationId: "",
    role: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    title: "",
    sendInvite: true,
    inviteExpiresInDays: 7,
    allowJobNotifications: true,
    allowDailySummary: false,
    requirePasswordResetOnFirstLogin: true,
  });

  const [errors, setErrors] = useState({});

  const associations = useMemo(() => {
    if (form.userType === "customer") return MOCK_CUSTOMERS;
    if (form.userType === "vendor") return MOCK_VENDORS;
    return [];
  }, [form.userType]);

  const filteredAssociations = useMemo(() => {
    const q = associationSearch.trim().toLowerCase();
    if (!q) return associations;
    return associations.filter((item) => item.name.toLowerCase().includes(q));
  }, [associationSearch, associations]);

  const selectedAssociation = useMemo(() => {
    return associations.find(
      (item) => String(item.id) === String(form.associationId),
    );
  }, [associations, form.associationId]);

  const availableRoles = useMemo(() => {
    return ROLE_OPTIONS[form.userType] || [];
  }, [form.userType]);

  const selectedRole = useMemo(() => {
    return availableRoles.find((r) => r.id === form.role);
  }, [availableRoles, form.role]);

  function updateField(name, value) {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  }

  function resetDownstreamAfterType(type) {
    setForm((prev) => ({
      ...prev,
      userType: type,
      associationId: "",
      role: "",
    }));
    setAssociationSearch("");
    setErrors({});
  }

  function validateCurrentStep() {
    const nextErrors = {};

    if (step === 1) {
      if (!form.userType) nextErrors.userType = "Please select a user type.";
    }

    if (step === 2) {
      if (
        (form.userType === "customer" || form.userType === "vendor") &&
        !form.associationId
      ) {
        nextErrors.associationId =
          "Please select an association before continuing.";
      }
    }

    if (step === 3) {
      if (!form.role) nextErrors.role = "Please choose a role.";
    }

    if (step === 4) {
      if (!form.firstName.trim())
        nextErrors.firstName = "First name is required.";
      if (!form.lastName.trim()) nextErrors.lastName = "Last name is required.";
      if (!form.email.trim()) {
        nextErrors.email = "Email is required.";
      } else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
        nextErrors.email = "Please enter a valid email address.";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  function handleNext() {
    if (!validateCurrentStep()) return;
    setStep((prev) => Math.min(prev + 1, 5));
  }

  function handleBack() {
    setStep((prev) => Math.max(prev - 1, 1));
  }

  function handleSubmit() {
    if (!validateCurrentStep()) return;

    const mockPayload = {
      first_name: form.firstName,
      last_name: form.lastName,
      email: form.email,
      phone: form.phone || null,
      title: form.title || null,
      user_type: form.userType,
      role: form.role,
      association_id: form.associationId || null,
      send_invite: form.sendInvite,
      invite_expires_in_days: form.inviteExpiresInDays,
      notifications: {
        job_notifications: form.allowJobNotifications,
        daily_summary: form.allowDailySummary,
      },
      require_password_reset_on_first_login:
        form.requirePasswordResetOnFirstLogin,
    };

    console.log("Mock user creation payload:", mockPayload);
    setSubmitted(true);
  }

  function renderHeader() {
    return (
      <div className="border-b border-slate-200 bg-gradient-to-r from-slate-50 via-white to-blue-50/60 px-6 py-6 sm:px-8">
        <div>
          <h1 className="mt-3 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Create User
          </h1>

          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 sm:text-base">
            Create a new user and assign the appropriate organization, role, and
            access settings.
          </p>

          <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Status
              </div>
              <div className="mt-2 text-sm font-bold text-slate-900">
                {submitted ? "Draft Submitted" : `Step ${step} of 5`}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Invite
              </div>
              <div className="mt-2 text-sm font-bold text-slate-900">
                {form.sendInvite ? "Send Automatically" : "Manual Later"}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Access
              </div>
              <div className="mt-2 text-sm font-bold text-slate-900">
                {selectedRole?.label || "Not Selected"}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Association
              </div>
              <div className="mt-2 truncate text-sm font-bold text-slate-900">
                {form.userType === "internal"
                  ? "Internal Team"
                  : selectedAssociation?.name || "Not Selected"}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderStepsRail() {
    return (
      <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="space-y-5">
          {STEP_TITLES.map((title, index) => {
            const number = index + 1;
            return (
              <div key={title}>
                <StepBadge
                  number={number}
                  title={title}
                  active={step === number}
                  complete={step > number}
                />
                {index !== STEP_TITLES.length - 1 ? (
                  <div className="ml-4 mt-3 h-6 w-px bg-slate-200" />
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="mt-8 rounded-2xl border border-blue-100 bg-blue-50 p-4">
          <div className="text-sm font-semibold text-blue-900">
            Before you continue
          </div>
          <p className="mt-2 text-sm leading-6 text-blue-800">
            Complete each step to define the user type, association, role, and
            invitation settings before creating the account.
          </p>
        </div>
      </div>
    );
  }

  function renderStep1() {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900">Select user type</h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Choose the type of user you want to create. Available roles and
            association options will update based on this selection.
          </p>
        </div>

        <div className="grid gap-4 grid-cols-1">
          {USER_TYPE_OPTIONS.map((option) => {
            const selected = form.userType === option.id;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => resetDownstreamAfterType(option.id)}
                className={cx(
                  "group rounded-3xl border p-5 text-left shadow-sm transition",
                  selected
                    ? "border-blue-500 bg-blue-50 shadow-md ring-4 ring-blue-100"
                    : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md",
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="text-base font-bold text-slate-900">
                      {option.label}
                    </div>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      {option.description}
                    </p>
                  </div>
                  <div
                    className={cx(
                      "mt-1 h-5 w-5 rounded-full border-2 transition",
                      selected
                        ? "border-blue-600 bg-blue-600"
                        : "border-slate-300 bg-white group-hover:border-slate-400",
                    )}
                  >
                    {selected ? (
                      <div className="flex h-full w-full items-center justify-center text-[10px] font-bold text-white">
                        ✓
                      </div>
                    ) : null}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {errors.userType ? (
          <p className="mt-4 text-sm font-medium text-red-600">
            {errors.userType}
          </p>
        ) : null}
      </div>
    );
  }

  function renderStep2() {
    const isInternal = form.userType === "internal";

    return (
      <div>
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900">
            Choose association
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Assign the user to the correct organization. Internal users do not
            require a customer or vendor association.
          </p>
        </div>

        {isInternal ? (
          <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-600 text-lg text-white">
                ✓
              </div>
              <div>
                <div className="text-base font-bold text-emerald-900">
                  Internal association confirmed
                </div>
                <p className="mt-2 max-w-2xl text-sm leading-6 text-emerald-800">
                  No customer or vendor selection is required for internal
                  users.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <Input
                label={`Search ${form.userType === "customer" ? "customer" : "vendor"}`}
                placeholder={
                  form.userType === "customer"
                    ? "Search customer accounts..."
                    : "Search vendor organizations..."
                }
                value={associationSearch}
                onChange={(e) => setAssociationSearch(e.target.value)}
              />
            </div>

            <div className="grid gap-3">
              {filteredAssociations.map((item) => {
                const selected = String(form.associationId) === String(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() =>
                      updateField("associationId", String(item.id))
                    }
                    className={cx(
                      "flex items-center justify-between rounded-2xl border p-4 text-left shadow-sm transition",
                      selected
                        ? "border-blue-500 bg-blue-50 ring-4 ring-blue-100"
                        : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md",
                    )}
                  >
                    <div>
                      <div className="text-sm font-bold text-slate-900">
                        {item.name}
                      </div>
                      <div className="mt-1 text-sm text-slate-500">
                        {form.userType === "customer"
                          ? "Customer account"
                          : "Vendor organization"}
                      </div>
                    </div>
                    <div
                      className={cx(
                        "flex h-8 w-8 items-center justify-center rounded-full border text-sm font-bold",
                        selected
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-slate-300 bg-white text-slate-400",
                      )}
                    >
                      {selected ? "✓" : ""}
                    </div>
                  </button>
                );
              })}

              {!filteredAssociations.length ? (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
                  <div className="text-sm font-semibold text-slate-700">
                    No results found
                  </div>
                  <div className="mt-1 text-sm text-slate-500">
                    Try a different search term.
                  </div>
                </div>
              ) : null}
            </div>

            {errors.associationId ? (
              <p className="mt-4 text-sm font-medium text-red-600">
                {errors.associationId}
              </p>
            ) : null}
          </>
        )}
      </div>
    );
  }

  function renderStep3() {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900">
            Assign role & access
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Select the role that best matches the user’s responsibilities and
            configure their initial access settings.
          </p>
        </div>

        <div className="grid gap-4 grid-cols-1">
          {availableRoles.map((role) => {
            const tone = getToneClasses(role.tone);
            const selected = form.role === role.id;

            return (
              <button
                key={role.id}
                type="button"
                onClick={() => updateField("role", role.id)}
                className={cx(
                  "rounded-3xl border p-5 text-left shadow-sm transition",
                  selected
                    ? `bg-white ring-4 ring-blue-100 border-blue-500`
                    : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-md",
                )}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-base font-bold text-slate-900">
                        {role.label}
                      </span>
                      <span
                        className={cx(
                          "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ring-1",
                          tone.soft,
                        )}
                      >
                        Role
                      </span>
                    </div>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      {role.description}
                    </p>
                  </div>

                  <div
                    className={cx(
                      "flex h-8 w-8 items-center justify-center rounded-full border text-sm font-bold",
                      selected
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-slate-300 bg-white text-slate-400",
                    )}
                  >
                    {selected ? "✓" : ""}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {errors.role ? (
          <p className="mt-4 text-sm font-medium text-red-600">{errors.role}</p>
        ) : null}

        <div className="mt-8 grid gap-4 xl:grid-cols-2">
          <Toggle
            checked={form.allowJobNotifications}
            onChange={(value) => updateField("allowJobNotifications", value)}
            label="Enable job notifications"
            description="Send notifications related to jobs, assignments, and updates."
          />

          <Toggle
            checked={form.allowDailySummary}
            onChange={(value) => updateField("allowDailySummary", value)}
            label="Enable daily summary"
            description="Send daily summary emails when applicable."
          />

          <Toggle
            checked={form.requirePasswordResetOnFirstLogin}
            onChange={(value) =>
              updateField("requirePasswordResetOnFirstLogin", value)
            }
            label="Require password reset"
            description="Require the user to reset their password after first login."
          />

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <label className="mb-2 block text-sm font-semibold text-slate-700">
              Invite expires in
            </label>
            <select
              value={form.inviteExpiresInDays}
              onChange={(e) =>
                updateField("inviteExpiresInDays", Number(e.target.value))
              }
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
            >
              <option value={3}>3 days</option>
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
            </select>
          </div>
        </div>
      </div>
    );
  }

  function renderStep4() {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900">
            Enter user details
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Enter the user’s contact and profile details.
          </p>
        </div>

        <div className="grid gap-5 md:grid-cols-2">
          <Input
            label="First Name"
            required
            placeholder="Enter first name"
            value={form.firstName}
            onChange={(e) => updateField("firstName", e.target.value)}
            error={errors.firstName}
          />
          <Input
            label="Last Name"
            required
            placeholder="Enter last name"
            value={form.lastName}
            onChange={(e) => updateField("lastName", e.target.value)}
            error={errors.lastName}
          />
          <div className="md:col-span-2">
            <Input
              label="Email Address"
              required
              type="email"
              placeholder="name@company.com"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
              error={errors.email}
            />
          </div>
          <Input
            label="Phone"
            placeholder="Optional"
            value={form.phone}
            onChange={(e) => updateField("phone", e.target.value)}
          />
          <Input
            label="Job Title"
            placeholder="Optional"
            value={form.title}
            onChange={(e) => updateField("title", e.target.value)}
          />
        </div>

        <div className="mt-8">
          <Toggle
            checked={form.sendInvite}
            onChange={(value) => updateField("sendInvite", value)}
            label="Send invitation immediately"
            description="Send an onboarding invitation as soon as the user is created."
          />
        </div>
      </div>
    );
  }

  function renderStep5() {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-xl font-bold text-slate-900">Review & confirm</h2>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            Review the information below before creating the user.
          </p>
        </div>

        <div className="grid gap-6 grid-cols-1">
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-b from-slate-50 to-white p-6 shadow-sm">
            <div className="text-lg font-bold text-slate-900">
              Access Snapshot
            </div>

            <div className="mt-5 space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Role
                </div>
                <div className="mt-2 text-sm font-bold text-slate-900">
                  {selectedRole?.label || "Not selected"}
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-500">
                  {selectedRole?.description ||
                    "Choose a role to preview access context."}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Invite Flow
                </div>
                <div className="mt-2 text-sm font-bold text-slate-900">
                  {form.sendInvite
                    ? "Automatic invitation enabled"
                    : "Invitation will be sent later"}
                </div>
              </div>

              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4">
                <div className="text-sm font-semibold text-blue-900">
                  Ready to submit
                </div>
                <p className="mt-2 text-sm leading-6 text-blue-800">
                  Once connected to the backend, this step will submit the user
                  creation request.
                </p>
              </div>
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-4">
              <div>
                <div className="text-lg font-bold text-slate-900">
                  Provisioning Summary
                </div>
                <div className="mt-1 text-sm text-slate-500">
                  Review all selections before submission
                </div>
              </div>
              <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
                Draft Payload
              </div>
            </div>

            <div>
              <SummaryRow
                label="User Type"
                value={
                  USER_TYPE_OPTIONS.find((u) => u.id === form.userType)?.label
                }
              />
              <SummaryRow
                label="Association"
                value={
                  form.userType === "internal"
                    ? "Internal Team"
                    : selectedAssociation?.name
                }
              />
              <SummaryRow label="Role" value={selectedRole?.label} />
              <SummaryRow label="First Name" value={form.firstName} />
              <SummaryRow label="Last Name" value={form.lastName} />
              <SummaryRow label="Email" value={form.email} />
              <SummaryRow label="Phone" value={form.phone} />
              <SummaryRow label="Job Title" value={form.title} />
              <SummaryRow
                label="Send Invite"
                value={form.sendInvite ? "Yes" : "No"}
              />
              <SummaryRow
                label="Invite Expiration"
                value={`${form.inviteExpiresInDays} day${form.inviteExpiresInDays > 1 ? "s" : ""}`}
              />
              <SummaryRow
                label="Job Notifications"
                value={form.allowJobNotifications ? "Enabled" : "Disabled"}
              />
              <SummaryRow
                label="Daily Summary"
                value={form.allowDailySummary ? "Enabled" : "Disabled"}
              />
              <SummaryRow
                label="Require Password Reset"
                value={form.requirePasswordResetOnFirstLogin ? "Yes" : "No"}
              />
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderSuccessState() {
    return (
      <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 shadow-sm">
        <div className="mx-auto max-w-2xl text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-emerald-600 text-2xl text-white shadow-lg">
            ✓
          </div>
          <h2 className="mt-5 text-2xl font-bold tracking-tight text-slate-900">
            User draft created successfully
          </h2>
          <p className="mt-3 text-sm leading-7 text-slate-600 sm:text-base">
            The mock submission completed successfully. When the backend is
            connected, this action will create the user and optionally send the
            invitation email.
          </p>

          <div className="mx-auto mt-8 max-w-xl rounded-3xl border border-slate-200 bg-white p-6 text-left shadow-sm">
            <SummaryRow
              label="User"
              value={`${form.firstName} ${form.lastName}`.trim()}
            />
            <SummaryRow label="Email" value={form.email} />
            <SummaryRow label="Role" value={selectedRole?.label} />
            <SummaryRow
              label="Association"
              value={
                form.userType === "internal"
                  ? "Internal Team"
                  : selectedAssociation?.name
              }
            />
            <SummaryRow
              label="Invite"
              value={
                form.sendInvite ? "Sent automatically" : "Saved without sending"
              }
            />
          </div>

          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => {
                setSubmitted(false);
                setStep(1);
                setAssociationSearch("");
                setErrors({});
                setForm({
                  userType: "",
                  associationId: "",
                  role: "",
                  firstName: "",
                  lastName: "",
                  email: "",
                  phone: "",
                  title: "",
                  sendInvite: true,
                  inviteExpiresInDays: 7,
                  allowJobNotifications: true,
                  allowDailySummary: false,
                  requirePasswordResetOnFirstLogin: true,
                });
              }}
              className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              Create Another User
            </button>
            <button
              type="button"
              onClick={() => setSubmitted(false)}
              className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
            >
              Go Back to Review
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6 xl:p-8">
      <div className="mx-auto max-w-7xl">
        <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
          {renderHeader()}

          <div className="grid gap-6 p-6 sm:p-8 xl:grid-cols-[300px_minmax(0,1fr)]">
            <div>{renderStepsRail()}</div>

            <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
              {submitted ? (
                renderSuccessState()
              ) : (
                <>
                  {step === 1 && renderStep1()}
                  {step === 2 && renderStep2()}
                  {step === 3 && renderStep3()}
                  {step === 4 && renderStep4()}
                  {step === 5 && renderStep5()}

                  <div className="mt-10 flex flex-col gap-3 border-t border-slate-200 pt-6 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-sm text-slate-500">
                      {step < 5
                        ? `Continue to ${STEP_TITLES[step]}`
                        : "Everything looks good. Submit the user draft."}
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row">
                      <button
                        type="button"
                        onClick={handleBack}
                        disabled={step === 1}
                        className={cx(
                          "inline-flex items-center justify-center rounded-xl px-5 py-3 text-sm font-semibold shadow-sm transition",
                          step === 1
                            ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400"
                            : "border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
                        )}
                      >
                        Back
                      </button>

                      {step < 5 ? (
                        <button
                          type="button"
                          onClick={handleNext}
                          className="inline-flex items-center justify-center rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700"
                        >
                          Continue
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={handleSubmit}
                          className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
                        >
                          Create User Draft
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
